/**
 * Data Sources: what the selected bot has been trained on.
 *
 * Crawling happens in the background, so rows arrive as `pending` and settle on
 * `trained` or `error`. The table polls while any row is still working and
 * stops once everything has settled — a permanent interval would keep a laptop
 * awake for a page nobody is watching.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  CorpusSections,
  DataSourceDetail,
  DataSourceListResponse,
  ExclusionPreview,
  RetrievedChunk,
  WebsiteScan,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { formatDateTime, formatNumber } from '../lib/format';
import { useBots } from '../hooks/useBots';

const POLL_INTERVAL_MS = 3_000;

type AddTab = 'website' | 'url' | 'sitemap' | 'file' | 'text' | 'qa' | 'youtube';

/** The "what / when" explainer shown at the top of each add-source tab. */
const TAB_HELP: Record<AddTab, { what: string; when: string }> = {
  website: {
    what: 'Finds every page on a site and trains on all of them, one row per page. Scan first to see the site’s sections and leave out the ones you don’t want.',
    when: 'you are setting up a bot for the first time, or want an entire site covered. This is the usual starting point.',
  },
  url: {
    what: 'Trains on just the one page you enter. Links on that page are not followed.',
    when: 'a single new or updated page is missing, or you only want one page from another site.',
  },
  sitemap: {
    what: 'Trains on exactly the pages listed in a sitemap file, or in a list of addresses you paste. Nothing else on the site is crawled.',
    when: 'you want to control precisely which pages are included, for example one product range.',
  },
  file: {
    what: 'Reads the text out of a document you upload and trains on it.',
    when: 'the information isn’t on a website, such as spec sheets, price lists, warranty terms or internal FAQs.',
  },
  text: {
    what: 'Trains on text you type or paste in directly.',
    when: 'you have a short piece of information with no page or file behind it, such as opening hours, a policy note, or a correction to something the bot gets wrong.',
  },
  qa: {
    what: 'Teaches the bot one question and the answer it should give.',
    when: 'the bot answers a common question badly or not at all. This is the most precise way to fix a specific answer.',
  },
  youtube: {
    what: 'Uses a YouTube video’s captions as training text. Only what is said in the video is learned, not what is shown.',
    when: 'a product demo or how-to video explains something your web pages don’t.',
  },
};

const TYPE_LABELS: Record<string, string> = {
  url: 'Web page',
  text: 'Plain text',
  qa: 'Q&A pair',
  file: 'File',
  youtube: 'YouTube video',
};

export function DataSources(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [data, setData] = useState<DataSourceListResponse | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState<DataSourceDetail | null>(null);
  const [probe, setProbe] = useState<RetrievedChunk[] | null>(null);
  const [probeQuery, setProbeQuery] = useState('');

  const load = useCallback(
    async (quiet = false) => {
      if (!slug) return;
      if (!quiet) setLoading(true);
      try {
        setData(await api.listDataSources(slug, search || undefined));
        setError(null);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not load sources');
      } finally {
        setLoading(false);
      }
    },
    [slug, search],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Poll only while something is still being crawled or embedded.
  const working = data?.items.some(
    (item) => item.status === 'pending' || item.status === 'crawling',
  );
  useEffect(() => {
    if (!working) return;
    const timer = window.setInterval(() => void load(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [working, load]);

  async function handleRetrainAll(): Promise<void> {
    if (!slug) return;
    await api.retrainAllDataSources(slug);
    await load(true);
  }

  async function handleRetrain(id: string): Promise<void> {
    if (!slug) return;
    await api.retrainDataSource(slug, id);
    await load(true);
  }

  async function handleDelete(id: string, name: string): Promise<void> {
    if (!slug) return;
    if (!window.confirm(`Delete "${name}" and everything trained from it?`)) return;
    await api.deleteDataSource(slug, id);
    await load(true);
  }

  async function handleProbe(): Promise<void> {
    if (!slug || !probeQuery.trim()) return;
    const result = await api.testRetrieval(slug, probeQuery);
    setProbe(result.chunks);
  }

  if (!slug) return <EmptyState title="No bot selected" />;

  return (
    <div>
      <PageHeader
        title="Data Sources"
        description={
          data
            ? `${data.trained_count} of ${data.total} trained · ${formatNumber(
                data.total_characters,
              )} characters`
            : 'What this assistant has been trained on'
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => void handleRetrainAll()}>
              Re-crawl all
            </Button>
            <Button onClick={() => setShowAdd(true)}>Add new</Button>
          </>
        }
      />

      {error && <ErrorNotice message={error} />}

      <Card className="mb-4 p-4">
        <label className="text-sm font-medium text-slate-700" htmlFor="probe">
          Test what this bot would retrieve
        </label>
        <p className="mt-0.5 text-xs text-slate-500">
          Type a question the way a visitor would. You&apos;ll see the passages the
          assistant would read before answering. If the right content doesn&apos;t
          appear, the bot can&apos;t answer the question well, so add it as a source. A
          lower <em>distance</em> means a closer match.
        </p>
        <div className="mt-2 flex gap-2">
          <input
            id="probe"
            value={probeQuery}
            onChange={(event) => setProbeQuery(event.target.value)}
            placeholder="e.g. what load rating do your workbenches have"
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue"
          />
          <Button onClick={() => void handleProbe()}>Test</Button>
        </div>
        {probe !== null && (
          <div className="mt-3 space-y-2">
            {probe.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nothing matched. With <em>restrict to data sources</em> on, the
                assistant will decline this question.
              </p>
            ) : (
              probe.map((chunk, index) => (
                <div
                  key={index}
                  className="rounded border border-slate-200 bg-slate-50 p-3 text-sm"
                >
                  <div className="mb-1 flex justify-between text-xs text-slate-500">
                    <span>{chunk.source_url ?? chunk.source_name}</span>
                    <span>distance {chunk.distance.toFixed(3)}</span>
                  </div>
                  <p className="text-slate-700">{chunk.content.slice(0, 300)}…</p>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      <SectionsPanel slug={slug} onChanged={() => void load(true)} />

      <ExclusionsPanel slug={slug} />

      <Card>
        <div className="border-b border-slate-200 p-3">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by name or URL…"
            className="w-full max-w-sm rounded-md border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-brand-blue"
          />
        </div>

        {loading ? (
          <Spinner />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No data sources yet"
            hint="Add a page, a sitemap, pasted text or a Q&A pair to train this assistant."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 text-right font-medium">Characters</th>
                  <th className="px-4 py-2 font-medium">Last updated</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="max-w-md px-4 py-2">
                      <div className="truncate font-medium text-slate-800">
                        {item.name}
                      </div>
                      {item.source_url && (
                        <div className="truncate text-xs text-slate-500">
                          {item.source_url}
                        </div>
                      )}
                      {item.error_message && (
                        <div
                          className={`mt-0.5 text-xs ${
                            item.status === 'trained' ? 'text-amber-600' : 'text-red-600'
                          }`}
                        >
                          {item.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {TYPE_LABELS[item.type] ?? item.type}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                      {formatNumber(item.char_count)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {formatDateTime(item.last_updated)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            void api
                              .getDataSource(slug, item.id)
                              .then((detail) => setViewing(detail))
                          }
                          className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRetrain(item.id)}
                          className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                        >
                          Re-crawl
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(item.id, item.name)}
                          className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showAdd && slug && (
        <AddSourceModal
          slug={slug}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            void load(true);
          }}
        />
      )}

      {viewing && (
        <Modal title={viewing.name} onClose={() => setViewing(null)} wide>
          <p className="mb-3 text-xs text-slate-500">
            {formatNumber(viewing.char_count)} characters · {viewing.chunk_count} chunks
          </p>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs text-slate-700">
            {viewing.raw_content}
          </pre>
        </Modal>
      )}
    </div>
  );
}

/**
 * The website sections this bot is trained on, as a tick-list.
 *
 * This is the direct way to edit a crawled website after the fact: every
 * top-level slug the corpus contains, with its page and character counts, and
 * a box to keep or drop it. Unticking is destructive — the section's pages are
 * deleted and the section joins the saved exclusions, so a later crawl of the
 * same site does not quietly bring it back.
 */
function SectionsPanel({
  slug,
  onChanged,
}: {
  slug: string;
  onChanged: () => void;
}): JSX.Element {
  const [data, setData] = useState<CorpusSections | null>(null);
  const [keep, setKeep] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.listSections(slug);
      setData(result);
      setKeep(
        Object.fromEntries(
          result.sections.map((section) => [section.path, !section.excluded]),
        ),
      );
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load sections');
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const sections = data?.sections ?? [];
  // Unticked and still present → delete it. Ticked but excluded → allow it back.
  const remove = sections.filter((s) => !keep[s.path] && !s.excluded);
  const restore = sections.filter((s) => keep[s.path] && s.excluded);
  const pagesToDelete = remove.reduce((sum, section) => sum + section.pages, 0);
  const dirty = remove.length > 0 || restore.length > 0;

  async function apply(): Promise<void> {
    if (pagesToDelete > 0) {
      const names = remove.map((section) => section.path).join(', ');
      if (
        !window.confirm(
          `Delete ${formatNumber(pagesToDelete)} trained page(s) in ${names}?\n\n` +
            'The pages are removed from the knowledge base and the section is ' +
            'excluded from future crawls. Getting them back means crawling ' +
            'the site again.',
        )
      ) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      const result = await api.editSections(
        slug,
        remove.map((section) => section.path),
        restore.map((section) => section.path),
      );
      setNote(
        `${formatNumber(result.deleted_pages)} page(s) removed` +
          (result.restored_sections.length > 0
            ? `, ${result.restored_sections.length} section(s) allowed again`
            : ''),
      );
      await load();
      onChanged();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not apply the changes',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Website sections</span>
        {data && (
          <span className="text-xs text-slate-500">
            {formatNumber(data.total_pages)} pages ·{' '}
            {formatNumber(data.total_characters)} characters
          </span>
        )}
      </div>

      <div className="p-4">
        {error && <ErrorNotice message={error} />}

        {!data ? (
          <Spinner />
        ) : sections.length === 0 ? (
          <p className="text-sm text-slate-500">
            No crawled pages yet. Add a website and its sections appear here.
          </p>
        ) : (
          <>
            <p className="mb-2 text-xs text-slate-500">
              Untick a section to delete its pages and keep it out of future crawls.
              Ticking one back on lifts the exclusion but does not restore deleted pages
              — re-add the website to crawl them again.
            </p>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {sections.map((section) => (
                <li key={section.path}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={keep[section.path] ?? true}
                      onChange={(event) =>
                        setKeep((current) => ({
                          ...current,
                          [section.path]: event.target.checked,
                        }))
                      }
                    />
                    <span
                      className={`flex-1 truncate font-mono text-sm ${
                        keep[section.path] ? 'text-slate-700' : 'text-slate-400'
                      }`}
                    >
                      {section.path === '/' ? '/ (home page)' : section.path}
                    </span>
                    {section.pages === 0 && (
                      <span className="whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                        removed
                      </span>
                    )}
                    <span className="w-24 whitespace-nowrap text-right text-xs text-slate-500">
                      {formatNumber(section.pages)} page
                      {section.pages === 1 ? '' : 's'}
                    </span>
                    <span className="w-24 whitespace-nowrap text-right text-xs text-slate-400">
                      {formatNumber(section.characters)} ch
                    </span>
                  </label>
                </li>
              ))}
            </ul>

            <div className="mt-3 flex items-center gap-3">
              <Button onClick={() => void apply()} disabled={busy || !dirty}>
                {busy ? 'Applying…' : 'Apply changes'}
              </Button>
              {dirty && (
                <span className="text-xs text-slate-500">
                  {remove.length > 0 &&
                    `${formatNumber(pagesToDelete)} page(s) will be deleted`}
                  {remove.length > 0 && restore.length > 0 && ' · '}
                  {restore.length > 0 && `${restore.length} section(s) allowed again`}
                </span>
              )}
              {!dirty && note && <span className="text-xs text-slate-500">{note}</span>}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Excluded pages and folders.
 *
 * Kept on the page rather than inside the add-source modal because exclusions
 * are a standing property of the bot: they apply to every future crawl, and to
 * every re-crawl of a page already in the table. Preview before save, because
 * a mistyped pattern here quietly stops two hundred pages refreshing.
 */
function ExclusionsPanel({ slug }: { slug: string }): JSX.Element {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExclusionPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .getExclusions(slug)
      .then(({ exclusions }) => {
        setSaved(exclusions);
        setText(exclusions.join('\n'));
      })
      .catch(() => setError('Could not load the exclusion list'));
  }, [slug]);

  function parsed(): string[] {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  async function run(save: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const patterns = parsed();
      const preview = save
        ? await api.saveExclusions(slug, patterns)
        : await api.previewExclusions(slug, patterns);
      setResult(preview);
      if (save) setSaved(preview.exclusions);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not apply the patterns',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-medium text-slate-700">
          Excluded pages and folders
        </span>
        <span className="text-sm text-slate-500">
          {saved.length === 0
            ? 'None — everything is crawled'
            : `${saved.length} pattern${saved.length === 1 ? '' : 's'}`}
          <span className="ml-2 text-slate-400">{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-200 p-4">
          {error && <ErrorNotice message={error} />}

          <label className="text-sm font-medium text-slate-700" htmlFor="exclusions">
            One pattern per line
          </label>
          <textarea
            id="exclusions"
            rows={5}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'/articles/\n/blog/*\n*/tag/*\nhttps://example.com/pricing'}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-brand-blue"
          />
          <p className="mt-1 text-xs text-slate-500">
            A bare folder such as <code>/articles/</code> excludes that section and
            everything under it. Globs (<code>*</code>, <code>?</code>) match the path
            or the whole URL. Saving never deletes pages that are already trained — it
            stops them being crawled again.
          </p>

          <div className="mt-3 flex gap-2">
            <Button variant="secondary" onClick={() => void run(false)} disabled={busy}>
              Preview
            </Button>
            <Button onClick={() => void run(true)} disabled={busy}>
              {busy ? 'Working…' : 'Save exclusions'}
            </Button>
          </div>

          {result && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="text-slate-700">
                {result.excluded} of {result.total} crawled pages match these patterns.
              </p>
              {result.examples.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
                  {result.examples.map((example) => (
                    <li key={example} className="truncate">
                      {example}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function AddSourceModal({
  slug,
  onClose,
  onAdded,
}: {
  slug: string;
  onClose: () => void;
  onAdded: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<AddTab>('website');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [homepage, setHomepage] = useState('');
  const [siteExclude, setSiteExclude] = useState('');
  const [saveExclusions, setSaveExclusions] = useState(false);
  const [savedPatterns, setSavedPatterns] = useState<string[]>([]);
  const [scan, setScan] = useState<WebsiteScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [url, setUrl] = useState('');
  const [sitemap, setSitemap] = useState('');
  const [urlList, setUrlList] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [textName, setTextName] = useState('');
  const [textBody, setTextBody] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [accept, setAccept] = useState('');

  useEffect(() => {
    void api
      .supportedFileTypes(slug)
      .then((types) => setAccept(types.extensions.join(',')))
      // Not fatal: without the hint the picker simply does not filter.
      .catch(() => setAccept(''));
  }, [slug]);

  // The bot's standing exclusions, shown here so it is obvious what a new
  // crawl will already leave out before anything is typed.
  useEffect(() => {
    void api
      .getExclusions(slug)
      .then(({ exclusions }) => setSavedPatterns(exclusions))
      .catch(() => setSavedPatterns([]));
  }, [slug]);

  /** The exclusion patterns currently typed into the box. */
  function excludeLines(): string[] {
    return siteExclude
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  /** Add or remove one section's pattern, keeping the box authoritative. */
  function toggleSection(pattern: string, on: boolean): void {
    const lines = excludeLines();
    const next = on
      ? lines.includes(pattern)
        ? lines
        : [...lines, pattern]
      : lines.filter((line) => line !== pattern);
    setSiteExclude(next.join('\n'));
  }

  async function runScan(): Promise<void> {
    if (!homepage.trim()) {
      setError('Enter the website address first.');
      return;
    }
    setScanning(true);
    setError(null);
    try {
      setScan(await api.scanWebsite(slug, homepage, excludeLines()));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not read that site');
    } finally {
      setScanning(false);
    }
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (tab === 'website') {
        await api.addWebsiteSource(slug, homepage, {
          exclude: siteExclude
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
          saveExclusions,
        });
      } else if (tab === 'url') {
        await api.addUrlSource(slug, url);
      } else if (tab === 'sitemap') {
        const list = urlList
          .split(/[\n,]/)
          .map((line) => line.trim())
          .filter(Boolean);
        await api.addUrlListSource(
          slug,
          list.length > 0 ? { urls: list } : { sitemap_url: sitemap },
        );
      } else if (tab === 'file') {
        if (!file) throw new Error('Choose a file to upload.');
        await api.addFileSource(slug, file);
      } else if (tab === 'text') {
        await api.addTextSource(slug, textName, textBody);
      } else if (tab === 'youtube') {
        await api.addYoutubeSource(slug, videoUrl);
      } else {
        await api.addQaSource(slug, question, answer);
      }
      onAdded();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not add the source');
    } finally {
      setBusy(false);
    }
  }

  const TABS: { id: AddTab; label: string }[] = [
    { id: 'website', label: 'Website' },
    { id: 'url', label: 'Single page' },
    { id: 'sitemap', label: 'Sitemap / URL list' },
    { id: 'file', label: 'Files' },
    { id: 'text', label: 'Plain text' },
    { id: 'qa', label: 'Q&A pair' },
    { id: 'youtube', label: 'YouTube' },
  ];

  const inputClass =
    'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue';

  return (
    <Modal title="Add data source" onClose={onClose} wide>
      <p className="mb-3 text-sm text-slate-600">
        A data source is content the assistant learns from. When a visitor asks a
        question, the assistant looks for the matching passages here and answers from
        them. Choose the option that matches where your content lives.
      </p>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`border-b-2 px-3 py-2 text-sm ${
              tab === item.id
                ? 'border-brand-blue font-medium text-brand-blue'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
        <p>
          <strong className="text-slate-700">What it does: </strong>
          {TAB_HELP[tab].what}
        </p>
        <p className="mt-1">
          <strong className="text-slate-700">Use it when: </strong>
          {TAB_HELP[tab].when}
        </p>
      </div>

      {error && <ErrorNotice message={error} />}

      {tab === 'website' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-home">
              Website homepage
            </label>
            <div className="flex gap-2">
              <input
                id="src-home"
                value={homepage}
                onChange={(event) => setHomepage(event.target.value)}
                placeholder="https://formaspace.com"
                className={`${inputClass} flex-1`}
              />
              <Button
                variant="secondary"
                onClick={() => void runScan()}
                disabled={scanning}
              >
                {scanning ? 'Scanning…' : scan ? 'Rescan' : 'Scan site'}
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Every page of the site is found and trained on — from its sitemap where it
              has one, otherwise by following links. robots.txt is always honoured. Scan
              first to see the site&apos;s sections and tick the ones to leave out.
            </p>
          </div>

          {scan && (
            <div className="rounded-md border border-slate-200">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm font-medium text-slate-700">
                  {formatNumber(scan.total_pages)} pages in {scan.sections.length}{' '}
                  section{scan.sections.length === 1 ? '' : 's'}
                </span>
                <span className="text-xs text-slate-500">
                  {scan.excluded_pages > 0
                    ? `${formatNumber(scan.excluded_pages)} excluded by the rules below`
                    : 'Nothing excluded yet'}
                </span>
              </div>
              {scan.truncated && (
                <p className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  The page limit was reached, so this site has more pages than are
                  listed. Section counts are a floor, not a total.
                </p>
              )}
              <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
                {scan.sections.map((section) => {
                  const ticked = excludeLines().includes(section.pattern);
                  return (
                    <li key={section.path}>
                      <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={ticked}
                          onChange={(event) =>
                            toggleSection(section.pattern, event.target.checked)
                          }
                        />
                        <span
                          className={`flex-1 truncate font-mono text-sm ${
                            ticked ? 'text-slate-400 line-through' : 'text-slate-700'
                          }`}
                          title={section.examples.join('\n')}
                        >
                          {section.path === '/' ? '/ (home page)' : section.path}
                        </span>
                        {!ticked && section.excluded_pages > 0 && (
                          <span className="whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                            {section.excluded === true
                              ? 'already excluded'
                              : `${section.excluded_pages} excluded`}
                          </span>
                        )}
                        <span className="w-20 whitespace-nowrap text-right text-xs text-slate-500">
                          {formatNumber(section.pages)} page
                          {section.pages === 1 ? '' : 's'}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-exclude">
              Exclude these pages or folders (optional)
            </label>
            {savedPatterns.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Already excluded for this bot:{' '}
                {savedPatterns.map((pattern) => (
                  <code
                    key={pattern}
                    className="mr-1 rounded bg-slate-100 px-1 py-0.5 text-slate-600"
                  >
                    {pattern}
                  </code>
                ))}
              </p>
            )}
            <textarea
              id="src-exclude"
              rows={4}
              value={siteExclude}
              onChange={(event) => setSiteExclude(event.target.value)}
              placeholder={'/articles/\n/blog/*'}
              className={`${inputClass} font-mono`}
            />
            <label className="mt-2 flex items-center gap-1.5 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={saveExclusions}
                onChange={(event) => {
                  setSaveExclusions(event.target.checked);
                  // Ticking this makes the box the whole list, so the bot's
                  // existing patterns are folded in rather than dropped.
                  if (event.target.checked) {
                    const lines = excludeLines();
                    const merged = [
                      ...savedPatterns.filter((p) => !lines.includes(p)),
                      ...lines,
                    ];
                    setSiteExclude(merged.join('\n'));
                  }
                }}
              />
              Replace this bot&apos;s saved exclusion list with these patterns
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Left unchecked, these apply to this crawl only and are added on top of the
              bot&apos;s saved exclusions.
            </p>
          </div>
        </div>
      )}

      {tab === 'url' && (
        <div>
          <label className="text-sm font-medium text-slate-700" htmlFor="src-url">
            Page URL
          </label>
          <input
            id="src-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://formaspace.com/workbenches/"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-slate-500">
            The full address of the page, including <code>https://</code>. If the page
            is already in the list, use <em>Re-crawl</em> on its row instead to refresh
            it.
          </p>
        </div>
      )}

      {tab === 'file' && (
        <div>
          <label className="text-sm font-medium text-slate-700" htmlFor="src-file">
            Document
          </label>
          <input
            id="src-file"
            type="file"
            accept={accept || undefined}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Word (.docx), Excel (.xlsx), PDF, CSV, and plain text. The text is extracted
            and trained on; the file itself is not stored. Scanned PDFs with no text
            layer are rejected — OCR them first.
          </p>
        </div>
      )}

      {tab === 'youtube' && (
        <div>
          <label className="text-sm font-medium text-slate-700" htmlFor="src-video">
            YouTube URL
          </label>
          <input
            id="src-video"
            value={videoUrl}
            onChange={(event) => setVideoUrl(event.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-slate-500">
            The video&apos;s captions are used as the training text. A video with
            captions disabled will show an error on its row.
          </p>
        </div>
      )}

      {tab === 'sitemap' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-sitemap">
              Sitemap URL
            </label>
            <input
              id="src-sitemap"
              value={sitemap}
              onChange={(event) => setSitemap(event.target.value)}
              placeholder="https://formaspace.com/sitemap_index.xml"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              A sitemap is a file listing a site&apos;s pages, usually at{' '}
              <code>/sitemap.xml</code> or <code>/sitemap_index.xml</code>. Every page
              it lists is added as its own row.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-urls">
              …or paste URLs, one per line
            </label>
            <textarea
              id="src-urls"
              rows={6}
              value={urlList}
              onChange={(event) => setUrlList(event.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              A URL list takes precedence over the sitemap field. robots.txt is always
              honoured.
            </p>
          </div>
        </div>
      )}

      {tab === 'text' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-name">
              Name
            </label>
            <input
              id="src-name"
              value={textName}
              onChange={(event) => setTextName(event.target.value)}
              placeholder="e.g. Shipping and lead times"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              A label so you can find this entry in the list later. Visitors never see
              it.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-body">
              Content
            </label>
            <textarea
              id="src-body"
              rows={10}
              value={textBody}
              onChange={(event) => setTextBody(event.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              Write it as plain, complete statements, the way you would want them
              repeated to a customer. The assistant can only use what is written here.
            </p>
          </div>
        </div>
      )}

      {tab === 'qa' && (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-q">
              Question
            </label>
            <input
              id="src-q"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="e.g. Do you ship outside the United States?"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              Phrase it the way a visitor would ask, using the words they are likely to
              use. The bot finds this entry by matching words, so a question worded very
              differently may not reach it. Add a second pair for common alternative
              phrasings.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700" htmlFor="src-a">
              Answer
            </label>
            <textarea
              id="src-a"
              rows={6}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-slate-500">
              The facts the assistant should give. It puts the reply in its own words,
              so make sure everything it needs is here, such as prices, timescales or
              who to contact.
            </p>
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} disabled={busy}>
          {busy ? 'Adding…' : 'Add and train'}
        </Button>
      </div>
    </Modal>
  );
}
